import { logger } from '@utils/logger';
import type { PaginationMetadata, RankedListMeta } from '@models/api/shared';

export type PageParam = { cursor: number } | { page: number };

/**
 * Shape every infinite question-list queryFn returns: IDs only (entities live
 * in the entity store, populated by upsertQuestionEntities in the same
 * queryFn) plus the pagination envelope. `TMeta` defaults to the cursor-protocol
 * `RankedListMeta`; pass `Record<string, unknown>` for endpoints whose meta
 * shape isn't the ranked-list contract (e.g. the space feed's `is_preview` meta).
 */
export interface NormalizedPage<TMeta = RankedListMeta> {
  ids: string[];
  pagination: PaginationMetadata;
  meta: TMeta | null | undefined;
}

interface PageWithMeta {
  // `object` (not `Record<string, unknown>`) so concrete interface types like
  // `RankedListMeta` — which has no index signature — remain assignable.
  // Narrowing via `'key' in meta` carries property access through correctly.
  meta?: object | null;
  pagination: PaginationMetadata;
}

interface ResolveOptions {
  hookName: string;
  allowIsPreviewFallback?: boolean;
}

/**
 * Shared pagination resolver for infinite-question hooks.
 *
 * Priority order:
 *   1. meta === null              → cursor exhausted
 *   2. meta has next_cursor       → cursor protocol; trust the value
 *   3. meta === undefined         → legacy endpoint; fall back to pagination.has_next
 *   4. meta has is_preview: false → space-member page-based response (opt-in via allowIsPreviewFallback)
 *   5. anything else              → log unexpected shape and terminate
 */
export function resolveNextPageParam(
  lastPage: PageWithMeta,
  allPages: PageWithMeta[],
  { hookName, allowIsPreviewFallback = false }: ResolveOptions,
): PageParam | undefined {
  if (lastPage.meta === null) return undefined;

  if (lastPage.meta !== undefined && 'next_cursor' in lastPage.meta) {
    return typeof lastPage.meta.next_cursor === 'number'
      ? { cursor: lastPage.meta.next_cursor }
      : undefined;
  }

  if (lastPage.meta === undefined && lastPage.pagination.has_next) {
    return { page: allPages.length + 1 };
  }

  if (
    allowIsPreviewFallback &&
    lastPage.meta != null &&
    'is_preview' in lastPage.meta &&
    lastPage.meta.is_preview === false &&
    lastPage.pagination.has_next
  ) {
    return { page: allPages.length + 1 };
  }

  const isUnexpected = allowIsPreviewFallback
    ? lastPage.meta != null && !('is_preview' in lastPage.meta)
    : lastPage.meta != null;

  if (isUnexpected) {
    logger.error(`[${hookName}] meta present but next_cursor missing — silent termination`, {
      meta: lastPage.meta,
    });
  }

  return undefined;
}
